import React, { useState } from 'react';
import { Trash2, Edit, Copy, Mail, RefreshCw, KeyRound } from 'lucide-react';
import { useOrganizersStore } from '../../stores/organizersStore';
import { Organizer } from '../../types/organizer';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { Flag } from '../common/Flag';
import { COUNTRY_CODES } from '../../config/constants';

type Props = {
  organizer: Organizer;
  onEdit: (organizer: Organizer) => void;
  onDelete: (organizer: Organizer) => void;
  onSyncName?: (organizerId: string) => void;
  onReplaceAuth?: (organizer: Organizer) => void;
};

export const OrganizerRow = ({ organizer, onEdit, onDelete, onSyncName, onReplaceAuth }: Props) => {
  const { translations } = useLanguage();
  const [isSyncing, setIsSyncing] = useState(false);

  const copyToClipboard = async (text: string, type: 'email') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(
        type === 'email'
          ? translations?.organizerRow?.copyEmailSuccess || 'Email copied'
          : translations?.organizerRow?.copySuccess || 'Copied to clipboard'
      );
    } catch (error) {
      toast.error(translations?.organizerRow?.copyError || 'Failed to copy');
    }
  };

  const handleSyncName = () => {
    if (!onSyncName) return;
    setIsSyncing(true);
    onSyncName(organizer.id);
    setTimeout(() => setIsSyncing(false), 2000); // Visual feedback
  };

  // Mobile view content
  const MobileContent = () => (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center">
            <span className="text-primary-600 font-medium">{organizer.name.charAt(0)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="font-medium">{organizer.name}</span>
            {organizer.country && <Flag countryCode={organizer.country} className="ml-1" />}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">{translations?.organizerRow?.emailLabel || 'Email:'}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm">{organizer.email}</span>
            <button
              onClick={() => copyToClipboard(organizer.email, 'email')}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <Copy size={14} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Country display for mobile */}
        {organizer.country && (
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Country:</span>
            <div className="flex items-center gap-2">
              <span className="text-sm">{getCountryNameFromCode(organizer.country)}</span>
              <Flag countryCode={organizer.country} size={16} />
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-3 border-t">
          {/* Replace Auth Button */}
          {onReplaceAuth && (
            <button
              onClick={() => onReplaceAuth(organizer)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <KeyRound size={14} />
              <span>{translations?.organizerRow?.replaceAuth || 'Replace Auth'}</span>
            </button>
          )}
          
          {/* Sync Name Button */}
          {onSyncName && (
            <button
              onClick={handleSyncName}
              disabled={isSyncing}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm ${
                isSyncing
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
              } rounded-lg transition-colors`}
            >
              <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
              <span>
                {isSyncing
                  ? translations?.organizerRow?.syncing || 'Syncing...'
                  : translations?.organizerRow?.syncName || 'Sync Name'}
              </span>
            </button>
          )}
          
          <button
            onClick={() => onDelete(organizer)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-500 bg-red-50 rounded-lg hover:bg-red-100"
          >
            <Trash2 size={14} />
            <span>{translations?.organizerRow?.delete || 'Delete'}</span>
          </button>
          <button
            onClick={() => onEdit(organizer)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-primary-500 bg-primary-50 rounded-lg hover:bg-primary-100"
          >
            <Edit size={14} />
            <span>{translations?.organizerRow?.edit || 'Edit'}</span>
          </button>
        </div>
      </div>
    </>
  );

  // Desktop view
  const DesktopView = () => (
    <tr className="hidden md:table-row">
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center">
            <span className="text-primary-600 font-medium">
              {organizer.name.charAt(0)}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="font-medium">{organizer.name}</span>
            {organizer.country && <Flag countryCode={organizer.country} className="ml-1" />}
          </div>
        </div>
      </td>
      
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <span className="text-gray-600">{organizer.email}</span>
          <button
            onClick={() => copyToClipboard(organizer.email, 'email')}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            title={translations?.organizerRow?.copyEmail || 'Copy Email'}
          >
            <Copy size={14} className="text-gray-500" />
          </button>
        </div>
      </td>

      {/* Country column for desktop */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          {organizer.country ? (
            <>
              <Flag countryCode={organizer.country} size={16} />
              <span className="text-gray-600">{getCountryNameFromCode(organizer.country)}</span>
            </>
          ) : (
            <span className="text-gray-400">Not specified</span>
          )}
        </div>
      </td>

      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          {/* Replace Auth Button */}
          {onReplaceAuth && (
            <button
              onClick={() => onReplaceAuth(organizer)}
              className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
              title={translations?.organizerRow?.replaceAuth || 'Replace Auth Credentials'}
            >
              <KeyRound size={18} />
            </button>
          )}
          
          {/* Sync Name Button */}
          {onSyncName && (
            <button
              onClick={handleSyncName}
              disabled={isSyncing}
              className={`p-1.5 rounded-lg transition-colors ${
                isSyncing ? 'text-gray-400 cursor-not-allowed' : 'text-blue-500 hover:bg-blue-50 hover:text-blue-600'
              }`}
              title={isSyncing ? 'Syncing...' : translations?.organizerRow?.syncName || 'Sync Name'}
            >
              <RefreshCw size={18} className={isSyncing ? 'animate-spin' : ''} />
            </button>
          )}
          
          <button
            onClick={() => onDelete(organizer)}
            className="text-red-500 hover:text-red-600"
            title={translations?.organizerRow?.delete || 'Delete'}
          >
            <Trash2 size={18} />
          </button>
          <button
            onClick={() => onEdit(organizer)}
            className="text-primary-500 hover:text-primary-600"
            title={translations?.organizerRow?.edit || 'Edit'}
          >
            <Edit size={18} />
          </button>
        </div>
      </td>
    </tr>
  );

  // Helper function to get country name from code
  const getCountryNameFromCode = (code: string): string => {
    return COUNTRY_CODES[code as keyof typeof COUNTRY_CODES] || code;
  };

  return (
    <>
      {/* Mobile View */}
      <div className="p-4 border-b md:hidden">
        <MobileContent />
      </div>

      {/* Desktop View */}
      <DesktopView />
    </>
  );
};